import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Writing and editing → Statutes panel.
// Fixture: `writingTools` (the Valley Mart report, finished; Identify Statutes
// answers a department section, two state sections, and one weak match).
const CASE_ID = "c0000000-0000-4000-8000-000000000201";
const TAB = `${sel("editorSidebarTabs")} button[title="Statutes"]`;
const ITEMS = sel("statuteItem");

const scene: Scene = {
  id: "statutes",
  title: "Statutes panel",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "writingTools",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "The Statutes tab reads your narrative and lists the sections the facts support. Treat it as a prompt for your own judgment, not a charging decision.",
      actions: [
        { type: "waitFor", target: sel("editorSidebarTabs") },
        { type: "wait", ms: 1200 },
        { type: "click", target: TAB },
        { type: "waitFor", target: sel("statuteIdentify") },
        { type: "spotlight", target: sel("statutesPanel") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "state",
      narration:
        "It needs your state first. If the button reads Set Statutes State, click it and choose your state under Include Statutes in Report Preferences. Mine is already set, so I'll click Identify Statutes.",
      actions: [
        { type: "moveTo", target: sel("statuteIdentify") },
        { type: "spotlight", target: sel("statuteIdentify") },
        { type: "wait", ms: 9300 },
        { type: "click", target: sel("statuteIdentify") },
        { type: "spotlight", target: null },
        { type: "waitFor", target: ITEMS, timeoutMs: 15000 },
      ],
    },
    {
      id: "results",
      narration:
        "The results are ranked by how well the facts match. Each one carries a tag: Strong Match, Match, or Weak Match.",
      actions: [
        { type: "waitFor", target: ITEMS },
        { type: "moveTo", target: `${ITEMS}:nth-of-type(3)` },
        { type: "spotlight", target: sel("statutesPanel") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "weak",
      narration:
        "A weak match usually means an element is missing from the narrative, not that the charge is wrong. Petty Theft with a Prior needs a prior conviction, and I never mentioned one.",
      actions: [
        { type: "scrollIntoView", target: `${ITEMS}:last-of-type` },
        { type: "wait", ms: 500 },
        { type: "moveTo", target: `${ITEMS}:last-of-type` },
        { type: "spotlight", target: `${ITEMS}:last-of-type` },
        { type: "wait", ms: 8000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "dept",
      narration:
        "A Dept badge means the result comes from my department's own documents, like municipal code. A department section that fits is listed first, and cited in the narrative instead of the state statute.",
      actions: [
        { type: "scrollIntoView", target: `${ITEMS}:nth-of-type(2)` },
        { type: "wait", ms: 500 },
        { type: "moveTo", target: `${ITEMS}:nth-of-type(2)` },
        { type: "spotlight", target: `${ITEMS}:nth-of-type(2)` },
        { type: "wait", ms: 9000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "menu",
      narration:
        "Click a result to add it to the report at the cursor, the top, or the end. A department section also has Open section, which shows the source page. A state statute has More Info.",
      actions: [
        { type: "click", target: `${ITEMS}:nth-of-type(2) > div` },
        { type: "wait", ms: 7500 },
        { type: "click", target: `${ITEMS}:nth-of-type(3) > div` },
        { type: "wait", ms: 2000 },
      ],
    },
    {
      id: "outro",
      narration: "Verify every result before it goes into an official report.",
      actions: [{ type: "wait", ms: 1500 }],
    },
  ],
};

export default scene;
